import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { fetchOpenPositions } from '../api/apiService';
import { isValidUuid } from './useOpenPositions';

/**
 * Live position sync over the trading platform's `/position-sync` WebSocket
 * feed, seeded and reconciled over REST via `fetchOpenPositions`.
 *
 * This mirrors the service-to-service `position-sync` protocol but runs in the
 * browser. Two things differ from the raw protocol and are handled here:
 *
 *  1. Field naming. The wire `PositionSyncSnapshot` uses camelCase
 *     (`positionId`, `symbol`, `entryPrice`, …) while this portal's REST API and
 *     positions table use snake_case (`id`, `symbol_name`, `entry_price`, …).
 *     Every snapshot — whether it arrives over the socket or over REST — is run
 *     through `normalizeSnapshot` so the stored map has ONE consistent,
 *     table-ready shape. That lets the existing `<table>` render unchanged.
 *
 *  2. Multi-account. The `/position-sync` gateway is keyed by a single
 *     `account_id`. The dashboard can own several trading accounts, so this
 *     hook opens one socket per owned account id (each scoped to its own
 *     account) and reconciles each account independently over REST. Incoming
 *     events are still defensively filtered to the owned account set.
 *
 * Reconnection: socket.io auto-reconnects, but the server does NOT replay
 * missed events, so on every (re)connect we re-fetch that account over REST to
 * heal a cache that may have gone stale while the socket was down.
 *
 * IMPORTANT (see position-sync.md): `/position-sync` is documented as a
 * backend, account-keyed feed. Before shipping to production, confirm with the
 * trading-platform team that browsers may connect to it (and which
 * `serverType` a browser should send). If it is backend-only, point the WS URL
 * at the user-keyed `NotificationGateway` instead — the state handling below is
 * identical; only the namespace URL, handshake query, and event name change.
 */

// ---------------------------------------------------------------------------
// Configuration (overridable per-env; see .env)
// ---------------------------------------------------------------------------

const env = (key, fallback = '') => process.env[key] ?? fallback;

const WS_URL = env('REACT_APP_NESTJS_WS_URL', 'http://localhost:3000');
const NAMESPACE = env('REACT_APP_POSITION_SYNC_NAMESPACE', '/position-sync');
const SOCKET_IO_PATH = env('REACT_APP_SOCKET_IO_PATH', '/crm/socket.io');
const DEFAULT_SERVER_TYPE = env('REACT_APP_POSITION_SYNC_SERVER_TYPE', 'express-server');
const WS_ENABLED_DEFAULT = env('REACT_APP_POSITION_SYNC_ENABLED', 'true') !== 'false';

// camelCase wire fields (for reference / pass-through guard in normalizeSnapshot)
const CAMEL_FIELDS = new Set([
  'positionId',
  'accountId',
  'symbol',
  'symbolName',
  'entryPrice',
  'currentPrice',
  'unrealizedPl',
  'stopLoss',
  'takeProfit',
  'marketValue',
  'marginUsed',
]);

/**
 * Normalize a position snapshot (wire camelCase OR portal snake_case) into the
 * canonical snake_case shape the dashboard table renders.
 *
 * Only fields that are actually present are written; absent fields are left
 * `undefined` (NOT set to null) so the merge step can restore the previous
 * value for partial `UPDATED` snapshots instead of clobbering it.
 *
 * @param {object} raw
 * @returns {object|null} normalized snapshot, or null if it has no usable id
 */
function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const positionId = raw.positionId ?? raw.id ?? raw.position_id;
  if (positionId === undefined || positionId === null) return null;

  const accountId = raw.accountId ?? raw.account_id;

  const n = { id: positionId, positionId };
  if (accountId !== undefined) {
    n.account_id = accountId;
    n.accountId = accountId;
  }

  // Pick the first defined value among candidate source keys.
  const pick = (target, ...vals) => {
    for (const v of vals) {
      if (v !== undefined) {
        n[target] = v;
        return;
      }
    }
  };

  pick('symbol_name', raw.symbol_name, raw.symbol, raw.symbolName);
  pick('side', raw.side);
  pick('qty', raw.qty);
  pick('entry_price', raw.entry_price, raw.entryPrice);
  pick('current_price', raw.current_price, raw.currentPrice);
  pick('unrealized_pl', raw.unrealized_pl, raw.unrealizedPl);
  pick('market_value', raw.market_value, raw.marketValue);
  pick('margin_used', raw.margin_used, raw.marginUsed);
  pick('stop_loss', raw.stop_loss, raw.stopLoss);
  pick('take_profit', raw.take_profit, raw.takeProfit);
  pick('status', raw.status);

  // Carry through any extra snake_case fields the REST layer returns that we
  // did not explicitly map (e.g. leverage, opened_at, …).
  for (const [k, v] of Object.entries(raw)) {
    if (v !== undefined && !(k in n) && !CAMEL_FIELDS.has(k)) {
      n[k] = v;
    }
  }

  return n;
}

/**
 * Merge a (possibly partial) snapshot into the previous one. A producer sends
 * only the fields it has in hand, so we never blow away fields we already know.
 * `undefined` values in `next` are treated as "not provided" and the previous
 * value is restored.
 *
 * @param {object|undefined} prev
 * @param {object} next
 * @returns {object}
 */
function mergeSnapshot(prev, next) {
  const merged = { ...(prev || {}), ...next };
  for (const k of Object.keys(next)) {
    if (next[k] === undefined && prev && prev[k] !== undefined) {
      merged[k] = prev[k];
    }
  }
  return merged;
}

/**
 * @typedef {Object} UsePositionSyncResult
 * @property {object[]} positions     Normalized positions (table-ready array).
 * @property {object}   positionsById positionId -> normalized snapshot map.
 * @property {number}   totalCount
 * @property {boolean}  loading       True during the initial REST seed.
 * @property {string|null} error
 * @property {'idle'|'connecting'|'online'|'offline'|'disabled'} status
 * @property {boolean}  connected     status === 'online'.
 * @property {object|null} lastEvent  Last PositionSyncEvent envelope received.
 * @property {() => void} refresh     Manually re-run the REST reconcile.
 */

/**
 * Subscribe to live position updates for one or more trading accounts.
 *
 * @param {object}   options
 * @param {string[]} [options.accountIds] Owned trading account UUIDs.
 * @param {string}   [options.accountId]  Single account (guide-faithful form).
 * @param {string}   [options.accessToken] Portal access token for REST calls.
 * @param {string}   [options.serverType] Handshake marker (see caveat above).
 * @param {boolean}  [options.enabled]    Seed + connect only when true.
 * @param {boolean}  [options.wsEnabled]  Toggle the WebSocket layer (the REST
 *                                        seed still runs). Defaults to env.
 * @param {number}   [options.reconcileIntervalMs] Optional safety-net REST
 *                                        poll; 0 (default) disables it.
 * @returns {UsePositionSyncResult}
 */
const usePositionSync = ({
  accountIds,
  accountId,
  accessToken,
  serverType = DEFAULT_SERVER_TYPE,
  enabled = true,
  wsEnabled = WS_ENABLED_DEFAULT,
  reconcileIntervalMs = 0,
} = {}) => {
  const [positionsById, setPositionsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle');
  const [lastEvent, setLastEvent] = useState(null);

  // De-duplicate + validate the owned account ids. Accepts either the array
  // (dashboard) or a single id (guide-faithful) form.
  const ids = useMemo(() => {
    const set = new Set();
    if (typeof accountId === 'string') set.add(accountId);
    (Array.isArray(accountIds) ? accountIds : []).forEach((id) => set.add(id));
    return [...set].filter(isValidUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(accountIds ?? []), accountId]);

  // Stable string key so effects only re-run when the owned set actually
  // changes (financialData can be replaced by WS updates with identical ids).
  const idsKey = ids.join(',');

  // Refs that socket callbacks read so they always see fresh values without
  // forcing socket reconnects on every render.
  const ownedRef = useRef(new Set(ids));
  ownedRef.current = new Set(ids);
  const socketsRef = useRef(new Map()); // accountId -> socket
  const onlineRef = useRef(new Set()); // accountId with an online socket
  const socketCountRef = useRef(0);
  const erroredRef = useRef(new Set()); // accountId whose last attempt errored
  /**
   * Reconcile a single account over REST: upsert the fetched open positions and
   * drop any local positions for that account that REST no longer reports as
   * open. Scoped to one account so multi-account reconciles don't clobber each
   * other. `silent` avoids touching the top-level `loading` flag (used on the
   * background reconnects, not the initial seed).
   */
  const reconcileAccount = useCallback(
    async (acctId, { silent = false } = {}) => {
      if (!accessToken || !acctId) return;
      try {
        const res = await fetchOpenPositions(accessToken, acctId);
        const rest = res && !res.error ? res.data?.positions : null;
        const normalized = (Array.isArray(rest) ? rest : [])
          .map(normalizeSnapshot)
          .filter(Boolean);
        const restIds = new Set(normalized.map((p) => p.id));

        setPositionsById((prev) => {
          const next = { ...prev };
          // Remove stale local positions for THIS account only.
          for (const [pid, pos] of Object.entries(prev)) {
            if (pos.account_id === acctId && !restIds.has(pid)) {
              delete next[pid];
            }
          }
          // Upsert fresh REST positions.
          for (const pos of normalized) {
            next[pos.id] = mergeSnapshot(prev[pos.id], pos);
          }
          return next;
        });
        if (!silent) setError(null);
      } catch (e) {
        if (!silent) setError('Unable to load positions.');
      }
    },
    [accessToken]
  );

  /** Apply a single normalized snapshot as an upsert (CREATED / UPDATED). */
  const applyUpsert = useCallback((snapshot) => {
    setPositionsById((prev) => ({
      ...prev,
      [snapshot.id]: mergeSnapshot(prev[snapshot.id], snapshot),
    }));
  }, []);

  /** Remove a position (CLOSED). */
  const applyRemove = useCallback((snapshot) => {
    setPositionsById((prev) => {
      if (!(snapshot.id in prev)) return prev;
      const next = { ...prev };
      delete next[snapshot.id];
      return next;
    });
  }, []);

  /** Handle one inbound `position-sync` envelope. */
  const onEvent = useCallback(
    (event) => {
      if (!event || typeof event !== 'object') return;
      setLastEvent(event);

      const owned = ownedRef.current;
      const snapshots = Array.isArray(event.data) ? event.data : [event.data];

      for (const snapshot of snapshots) {
        const normalized = normalizeSnapshot(snapshot);
        if (!normalized) continue;

        // Fall back to the envelope accountId if the snapshot omits it.
        const acct = normalized.account_id ?? event.accountId;
        if (acct !== undefined) normalized.account_id = acct;

        // Defensive: only apply positions for accounts this client owns (when
        // the owned set is known).
        if (owned.size > 0 && acct !== undefined && !owned.has(acct)) continue;

        if (event.type === 'POSITION_CLOSED') applyRemove(normalized);
        else applyUpsert(normalized);
      }
    },
    [applyUpsert, applyRemove]
  );

  // Recompute the aggregate connection status from the per-socket bookkeeping.
  const recomputeStatus = useCallback(() => {
    if (!wsEnabled || socketCountRef.current === 0) {
      setStatus(wsEnabled && ids.length > 0 ? 'connecting' : 'disabled');
      return;
    }
    if (onlineRef.current.size > 0) {
      setStatus('online');
    } else if (erroredRef.current.size >= socketCountRef.current) {
      setStatus('offline');
    } else {
      setStatus('connecting');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsEnabled, ids.length]);

  // -----------------------------------------------------------------------
  // Initial REST seed (runs even when the WS layer is disabled/offline).
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || ids.length === 0 || !accessToken) {
      setPositionsById((prev) => (Object.keys(prev).length ? {} : prev));
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(ids.map((id) => reconcileAccount(id, { silent: true })))
      .then(() => {
        if (!cancelled) setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setError('Unable to load positions.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, accessToken, reconcileAccount]);

  // -----------------------------------------------------------------------
  // WebSocket layer: one scoped socket per owned account.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !wsEnabled || ids.length === 0 || !WS_URL) {
      setStatus(enabled && wsEnabled && ids.length > 0 ? 'connecting' : 'disabled');
      return undefined;
    }

    socketCountRef.current = ids.length;
    onlineRef.current = new Set();
    erroredRef.current = new Set();
    setStatus('connecting');

    ids.forEach((acctId) => {
      const socket = io(WS_URL + NAMESPACE, {
        path: SOCKET_IO_PATH,
        query: { serverType, accountId: acctId },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });
      socketsRef.current.set(acctId, socket);

      const onConnect = () => {
        onlineRef.current.add(acctId);
        erroredRef.current.delete(acctId);
        // Announce the account and heal any cache that went stale while down.
        socket.emit('register-server', { accountId: acctId });
        reconcileAccount(acctId, { silent: true });
        recomputeStatus();
      };
      const onDisconnect = () => {
        onlineRef.current.delete(acctId);
        recomputeStatus();
      };
      const onError = () => {
        onlineRef.current.delete(acctId);
        erroredRef.current.add(acctId);
        recomputeStatus();
      };
      const onPositionSync = (event) => onEvent(event);

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onError);
      socket.on('position-sync', onPositionSync);
    });

    return () => {
      socketsRef.current.forEach((socket) => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('connect_error');
        socket.off('position-sync');
        socket.close();
      });
      socketsRef.current = new Map();
      onlineRef.current = new Set();
      erroredRef.current = new Set();
      socketCountRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wsEnabled, idsKey, serverType, accessToken, reconcileAccount, onEvent]);

  // -----------------------------------------------------------------------
  // Optional safety-net REST poll (off by default — WS is the live path).
  // -----------------------------------------------------------------------
  const refresh = useCallback(() => {
    ids.forEach((id) => reconcileAccount(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reconcileAccount]);

  useEffect(() => {
    if (!enabled || !accessToken || !reconcileIntervalMs || ids.length === 0) {
      return undefined;
    }
    const timer = setInterval(refresh, reconcileIntervalMs);
    return () => clearInterval(timer);
  }, [enabled, accessToken, reconcileIntervalMs, idsKey, refresh]);

  const positions = useMemo(() => Object.values(positionsById), [positionsById]);

  return {
    positions,
    positionsById,
    totalCount: positions.length,
    loading,
    error,
    status,
    connected: status === 'online',
    lastEvent,
    refresh,
  };
};

export default usePositionSync;


